/* eslint-disable no-param-reassign */
import React from "react";

import { Popover } from "antd";

import CurrencySymbol from "../Components/CurrencySymbol";

const MAX_LIMIT_FOR_K_FORMAT = 99999;

/* eslint-disable no-param-reassign */
/**
 * @param {Number} num The actual number to be formatted
 * @param {String} format Format of number expected with comma | default='NUM_INR'
 * @param {String} digits SHORT - 3.4M, LONG - 3,400,000 | default='LONG'
 * @param {Number} startLimit default = 9999
 * @returns {Number} Formatted number with proper symbol indicating value
 *
 * @example
 * newNumberFormat(earnings, 'NUM_INR')
 * // returns a number with Indian styled commas
 */
export default function newNumberFormat(
  num,
  format = "NUM_INR",
  digits = "LONG",
  startLimit = 9999
) {
  if (!num) return "0";

  if (!Number.isInteger(num)) {
    num = Number(parseFloat(num?.toFixed(2)));
  }

  if (num <= startLimit) return num;

  const locale = {
    NUM_INR: "en-IN",
    NUM_US: "en-US",
    NUM_EUR: "en-DE",
  };

  const options = {
    LONG: {
      notation: "standard",
      maximumFractionDigits: 2,
      // minimumFractionDigits: 2,
    },
    SHORT: {
      notation: "compact",
      maximumFractionDigits: 2,
      // minimumFractionDigits: 2,
    },
  };

  const numSystem = Intl.NumberFormat(locale[format], options[digits]);

  const item = numSystem.format(parseFloat(num?.toFixed(2)));

  return item;
}

/**
 * Formats a numeric amount with appropriate currency, compact representation, and tooltip.
 */
export const numFormatter = (data) => {
  const {
    amount = 0,
    startLimit = 9999,
    currencyIcon = null,
    hideCurrency = false,
    hideTooltip = false,
  } = data;

  const currency = data.currency?.toUpperCase() || "INR";
  if (!amount) return 0;

  let numAmount = amount;
  const numFormat = `NUM_${currency === "USD" ? "US" : currency}`;

  if (amount >= startLimit && amount <= MAX_LIMIT_FOR_K_FORMAT) {
    numAmount = numFormatterInUnit(amount, startLimit);
  } else {
    numAmount = newNumberFormat(
      amount,
      numFormat,
      amount > MAX_LIMIT_FOR_K_FORMAT ? "SHORT" : "LONG",
      startLimit
    );
  }

  const actualAmount = newNumberFormat(amount, numFormat, "LONG", 9999);

  const formattedAmount = (
    <>
      {!hideCurrency &&
        (currencyIcon || <CurrencySymbol currency={currency} />)}
      {numAmount}
    </>
  );
  const formattedActualAmount = (
    <>
      {!hideCurrency &&
        (currencyIcon || <CurrencySymbol currency={currency} />)}
      {actualAmount}
    </>
  );

  if (hideTooltip) return formattedAmount;

  return (
    <Popover content={formattedActualAmount}>
      <span>{formattedAmount}</span>
    </Popover>
  );
};

/**
 * Formats a number in a compact representation using units such as K, M, or GB.
 */
export const numFormatterInUnit = (
  num,
  startLimit = 999,
  digits = 2,
  type = "amount"
) => {
  try {
    if (!num) return "0";
    if (num <= startLimit) return parseFloat(num.toFixed(digits)).toString();

    const lookup =
      type === "fileSize"
        ? [
            { value: 1, symbol: "B" },
            { value: 1e3, symbol: "KB" },
            { value: 1e6, symbol: "MB" },
            { value: 1e9, symbol: "GB" },
            { value: 1e12, symbol: "TB" },
            { value: 1e15, symbol: "PB" },
            { value: 1e18, symbol: "EB" },
          ]
        : [
            { value: 1, symbol: "" },
            { value: 1e3, symbol: "K" },
            { value: 1e6, symbol: "M" },
            { value: 1e9, symbol: "B" },
            { value: 1e12, symbol: "T" },
          ];

    const rx = /\.0+$|(\.[0-9]*[1-9])0+$/;
    const item = lookup
      .slice()
      .reverse()
      .find((i) => num >= i.value);

    return item
      ? parseFloat((num / item.value).toFixed(digits).replace(rx, "$1")) +
          item.symbol
      : "0";
  } catch {
    return num.toString();
  }
};

/**
 * Formats a number based on locale and display style.
 */
export const numFormatterInLocale = (
  num,
  format,
  digits = "LONG",
  startLimit = 9999
) => {
  if (!num) return "0";
  if (!Number.isInteger(num)) num = Number(parseFloat(num.toFixed(2)));
  if (num <= startLimit) return num.toString();

  const numFormat = format || "NUM_INR";

  const localeMap = {
    NUM_INR: "en-IN",
    NUM_US: "en-US",
    NUM_EUR: "de-DE",
  };

  const formatOptions = {
    LONG: { notation: "standard", maximumFractionDigits: 2 },
    SHORT: { notation: "compact", maximumFractionDigits: 2 },
  };

  return new Intl.NumberFormat(
    localeMap[num > MAX_LIMIT_FOR_K_FORMAT ? numFormat : "NUM_US"],
    formatOptions[digits]
  ).format(num);
};

export const getNumFormatType = (currency) =>
  currency === "USD" ? "NUM_US" : `NUM_${currency}`;

export const formatNumberToReadable = (num) => {
  return Number(num).toLocaleString("en-IN");
};

export const formatIndianCurrencyToReadableWords = (amount) => {
  const num = Number(amount);

  // handling the crore
  if (num >= 1e7) {
    // crore or more (10 million = 1 crore)
    return `${formatNumberToReadable(
      (num / 1e7).toFixed(2).replace(/\.?0+$/, "")
    )} cr`;
  }

  // handling the lakh
  if (num >= 1e5) {
    // lakh or more (100,000)
    return `${formatNumberToReadable(
      (num / 1e5).toFixed(2).replace(/\.?0+$/, "")
    )} lakh`;
  }

  return `₹${num.toLocaleString("en-IN")}`;
};
